import { CSSProperties, MouseEvent, PropsWithChildren, forwardRef, useCallback } from 'react'
import { useAsRef } from 'src/hooks'
import styles from './style.module.scss'

export interface IconWrapperProps extends PropsWithChildren<any> {
  id?: string
  width?: number
  height?: number
  opacity?: number
  color?: string
  background?: string
  disabled?: boolean
  active?: {
    color?: string
    background?: string
  }
  onClick?: (e: MouseEvent<HTMLElement>) => void
  className?: string
  style?: CSSProperties
  text?: string
}

export const IconWrapper = forwardRef<HTMLDivElement, IconWrapperProps>(({ id, onClick, children, ...props }, ref) => {
  const { text = '' } = props

  const createRipple = useCallback((id: string) => {
    const icon = document.getElementById(id)

    if (!icon) {
      return
    }

    const circle = document.createElement('span')
    const diameter = Math.max(icon.clientWidth, icon.clientHeight)
    circle.style.width = circle.style.height = `${diameter}px`
    circle.classList.add(styles.ripple)

    icon.classList.add(styles.iconActive)
    const existingRipple = icon.getElementsByClassName(styles.ripple)[0] as HTMLElement
    existingRipple?.remove?.()
    icon.appendChild(circle)

    setTimeout(() => {
      icon.classList.remove(styles.iconActive)
    }, 500)
  }, [])

  const onClickRef = useAsRef(onClick)
  const handleClick = useCallback((e: MouseEvent<HTMLElement>) => {
    id && createRipple(id)
    onClickRef.current?.(e)
  }, [createRipple, id, onClickRef])

  // Set CSS variables based on props for dynamic styling
  const cssVariables: Record<string, string | null> = {
    '--icon-width': props.width ? `${props.width}px` : null,
    '--icon-height': props.height ? `${props.height}px` : null,
    '--icon-opacity': props.opacity !== undefined ? `${props.opacity}` : null,
    '--icon-color': props.color || null,
    '--icon-background': props.background || null,
    '--icon-pointer-events': props.disabled ? 'none' : null,
    '--active-background': props.active?.background || null
  }

  // Filter out undefined values
  const filteredCssVars = Object.fromEntries(
    Object.entries(cssVariables).filter(([_, value]) => value !== null)
  )

  const style = { ...filteredCssVars, ...props.style }
  const className = `${styles.icon} ${props.disabled ? styles.disabled : ''} ${props.className || ''}`

  return (
    <div
      ref={ref}
      id={id}
      className={className}
      style={style}
      onClick={handleClick}
    >
      {children}
      {text}
    </div>
  )
})
